import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import api from '../../services/api';
import type { NewsRunProgress, NewsRunStage } from '../../types';

/**
 * How far through a run we are, while it happens.
 *
 * A briefing is minutes of fetching, tagging and judging behind a single
 * activity chip, and silence reads as a hang. This shows the pipeline's own
 * arithmetic: 40 of 87 tagged, round 2 of 6.
 *
 * TWO SOURCES, ONE PICTURE, and the order matters. The GET is authoritative —
 * every number in it is derived from columns the pipeline already wrote, so a
 * page opened or reloaded mid-run has the complete state immediately. The
 * websocket then patches single stages as they move. That way round, a dropped
 * socket degrades to a slightly stale bar rather than a blank one.
 *
 * The poll is the safety net for exactly that: WebSocketContext has no replay,
 * so a reconnect silently loses whatever happened while it was gone. Fifteen
 * seconds is far cheaper than the run it is watching and stops only when the
 * run does.
 */

const POLL_MS = 15_000;

/** Which count on the snapshot each stage reads. `fetch` has no separate
 *  number — the pool existing IS the fetch being done. */
const STAGE_DONE: Record<NewsRunStage, keyof NewsRunProgress | null> = {
  fetch: null,
  hydrate: 'hydrated',
  tag: 'tagged',
  cluster: 'clustered',
  judge: 'judged',
};

export default function BriefingProgress({
  briefingId,
  running,
}: {
  briefingId: number;
  /** Drives the poll and the pulse. The bar stays visible when false so the
   *  final state is readable rather than vanishing at the moment it completes. */
  running: boolean;
}) {
  const { subscribe } = useWebSocket();
  const [progress, setProgress] = useState<NewsRunProgress | null>(null);
  const [stages, setStages] = useState<{ key: NewsRunStage; label: string }[]>([]);
  // Stage-level overrides from the socket, cleared whenever the GET lands.
  const [live, setLive] = useState<Partial<Record<NewsRunStage, number>>>({});
  const runningRef = useRef(running);
  runningRef.current = running;

  const load = useCallback(async () => {
    try {
      const log = await api.getNewsBriefingRunLog(briefingId);
      setProgress(log.progress);
      setStages(log.stages ?? []);
      setLive({});
    } catch {
      /* a failed refresh leaves the last good picture up */
    }
  }, [briefingId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return subscribe((event: any) => {
      if (event?.event_type !== 'news_beat' || event.briefing_id !== briefingId) {
        return;
      }
      if (event.status === 'progress' && event.progress) {
        const { stage, done, total, pass } = event.progress;
        setLive((prev) => ({ ...prev, [stage as NewsRunStage]: done }));
        setProgress((prev) =>
          prev
            ? { ...prev, total: total || prev.total, pass: pass ?? prev.pass }
            : prev,
        );
      }
      // Terminal: the derived half is what settles, so re-read it.
      if (['finished', 'failed', 'cancelled'].includes(event.status)) load();
    });
  }, [subscribe, briefingId, load]);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      if (runningRef.current) load();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [running, load]);

  if (!progress || !progress.total || stages.length === 0) return null;

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {running ? 'Building this briefing' : 'How far this run got'}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {progress.total} article{progress.total === 1 ? '' : 's'}
          {progress.pass ? ` · round ${progress.pass} of ${progress.of}` : ''}
        </span>
      </div>

      <ol className="grid gap-2 sm:grid-cols-5">
        {stages.map((stage) => {
          const field = STAGE_DONE[stage.key];
          const done =
            live[stage.key] ??
            (field ? (progress[field] as number) : progress.total);
          const pct = Math.min(
            100,
            Math.round((done / Math.max(progress.total, 1)) * 100),
          );
          const complete = done >= progress.total;
          return (
            <li key={stage.key}>
              <div className="flex items-baseline justify-between gap-1">
                <span
                  className={`text-[11px] ${
                    complete
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {stage.label}
                </span>
                <span className="text-[11px] tabular-nums text-gray-400 dark:text-gray-500">
                  {done}/{progress.total}
                </span>
              </div>
              <div className="mt-0.5 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full transition-[width] duration-500 ${
                    complete
                      ? 'bg-emerald-500'
                      : running
                        ? 'bg-indigo-500 animate-pulse'
                        : 'bg-gray-400 dark:bg-gray-500'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
